import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doFind, doLogin, loginSuccess } from '../../store/auth.slice';
import { useSnackbar } from 'notistack';
import Spinner from '../../components/Spinner';
import * as cookie from '../../lib/cookie';
import useNotification from '../../hooks/useNotification';
import { getWebUrl } from '../../lib/util';
import { Checkbox } from 'antd';
import { Button as AntButton, DatePicker, Descriptions, Modal, Select } from 'antd';
import CertificateModal from '../../components/modals/CertificateModal';
import { resetVerify } from '../../store/verify.slice';
import { RootState } from '../../store/rootReducer';
import styled from 'styled-components';
import { isFinalStep } from '../../store/find.slice';

export const enum ACCOUNT_STATUS_TYPE {
  FIND_ID,
  FIND_PASSWORD,
  LOGIN
}

const LoginCustomModal = styled(Modal)`
  & .ant-modal-body {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0;
      margin: 0;
  }
`


const Login: FC = () => {
  const { t } = useTranslation();
  const { closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { infoNotification, warningNotification, errorNotification } = useNotification();
  const { isVerify } = useSelector((state: RootState) => state.verify);
  const [webId, setWebId] = useState('');
  const [webPass, setWebPass] = useState('');
  const [toRemember, setToRemember] = useState(false);
  const [verifyType, setVerifyType] = useState(ACCOUNT_STATUS_TYPE.FIND_ID);
  const [loginCertificationVisible, setLoginCertificationVisible] = useState(false);
  const [isLogin, setIsLogin] = useState(false);

  const inputIdRef = useRef(null);


  useEffect(() => {
    const _webId = cookie.get('webId');
    if (_webId) {
      setWebId(_webId);
      setToRemember(true);
    }

    return () => {
      const snackbarKey = sessionStorage.getItem("snackbarKey");
      if (snackbarKey) {
        closeSnackbar(Number(snackbarKey));
        sessionStorage.removeItem("snackbarKey");
      }
    }
  }, []);

  useEffect (()=> {
    const isLoginFindComplete = checkLoginFindComplete();
    const isLoginVerifyComplete = checkLoginVerifyComplete();

    if(isLoginFindComplete) {
      cookie.set("twoFactorWebId", webId, 24);
      onLogin();
    } else if (isLoginVerifyComplete){
      onSetFindModal(ACCOUNT_STATUS_TYPE.LOGIN);
      setLoginCertificationVisible(true);
    }
  }, [isLogin, isVerify])

  const checkLoginFindComplete = () => isLogin && isVerify && verifyType === ACCOUNT_STATUS_TYPE.LOGIN;
  const checkLoginVerifyComplete = () => isLogin && !isVerify && verifyType === ACCOUNT_STATUS_TYPE.LOGIN;

  const onSubmit = async () => {
    if (!webId || !webPass) {
      return infoNotification(t('auth.login.message1'));
    }

    const res = await dispatch(doFind({ webId, webPass }));

    switch (res.status) {
      case 200:
        if(process.env.REACT_APP_IS_DEV === 'Y') {
          onLogin();
        } else {
          const twoFactorWebId = cookie.get('twoFactorWebId');
          if(twoFactorWebId === webId) {
            onLogin();
          } else {
            setVerifyType(ACCOUNT_STATUS_TYPE.LOGIN);
            setIsLogin(true);
          }
        }
        break;
      case 400:
        warningNotification(t('auth.login.message2'));
        break;
      case 403:
        if (res?.data?.data?.status === 'LO') {
          window.location.href = `${getWebUrl()}/canb/auth/verify`;
        }
        break;
      case 404:
        warningNotification(t('auth.login.message3'));
        break;
      default:
        errorNotification(res?.data?.data || t('common.message.error'));
    }

    //submitExecute(doLogin({ webId, webPass }));
  }


  const onLogin = async () => {
    const res = await dispatch(doLogin({ webId, webPass }));
    switch(res.status) {
      case 200: {
        toRemember ? cookie.set('webId', webId, 720) : cookie.remove('webId');
        dispatch(loginSuccess(res.data));
        break;
      }
      case 400:
        warningNotification(t('auth.login.message2'));
        break;
      case 403:
        if (res?.data?.data?.status === 'LO') {
          window.location.href = `${getWebUrl()}/canb/auth/verify`;
        }
        break;
      case 404:
        warningNotification(t('auth.login.message3'));
        break;
      default:
        errorNotification(res?.data?.data || t('common.message.error'));
    }
  }

  const onSetFindModal = (verifyType: ACCOUNT_STATUS_TYPE) => {
    setVerifyType(verifyType);
    setLoginCertificationVisible(true);
  }

  return (
    <>
      {/* {submitIsLoading && <Spinner />} */}
      <Helmet>
        <title>{t('auth.login.title')}</title>
      </Helmet>
      <div className="content scrollbar">
        <div className="login-wrap">
          <div className="login-edge">
            <div className="bx-row">
              <img src="../images/logo-sum-login.png" className="logo" />
            </div>
            <div className="bx-row">
              <div className="bx-inner__input">
                <span className="icon-id"></span>
                <input
                  type="text"
                  placeholder={t('auth.login.label1')}
                  onChange={e => setWebId(e.target.value)}
                  onKeyDown={e => {
                    const key = e.code;
                    if (key === 'Enter') {
                      onSubmit();
                    }
                  }}
                  value={webId}
                  ref={inputIdRef}
                />
              </div>
            </div>
            <div className="bx-row">
              <div className="bx-inner__input">
                <span className="icon-psw"></span>
                <input
                  type="password"
                  placeholder={t('auth.login.label2')}
                  value={webPass}
                  onKeyDown={e => {
                    const key = e.code;
                    if (key === 'Enter') {
                      onSubmit();
                    }
                  }}
                  onChange={e => setWebPass(e.target.value)}
                />
              </div>
            </div>
            <div className="bx-row">
              <button type="button" className="btn-basic pink" onClick={onSubmit}>
                {t('auth.login.title')}
              </button>
            </div>
            <div className="bx-row d-flex justify-content-between">
              <Checkbox
                className="align-items-center"
                checked={toRemember}
                onChange={() => setToRemember(!toRemember)}
              >
                {t('auth.login.checkbox')}
              </Checkbox>
              <span className="bx-inner__find">
                <a href="#" onClick={() => {
                  setLoginCertificationVisible(true);
                  onSetFindModal(ACCOUNT_STATUS_TYPE.FIND_ID);
                }}>{t('auth.login.button1')}</a>
                <a href="#" onClick={() => {
                  setLoginCertificationVisible(true);
                  onSetFindModal(ACCOUNT_STATUS_TYPE.FIND_PASSWORD);
                }}>{t('auth.login.button2')}</a>
              </span>
            </div>
          </div>
        </div>
      </div>
      { loginCertificationVisible && (
        <LoginCustomModal
          open={loginCertificationVisible}
          closable={false}
          footer={null}
        >
          <CertificateModal
            verifyType={verifyType}
            setLoginCertificationVisible={setLoginCertificationVisible}
            inputIdRef={inputIdRef}
            onSetFindModal={onSetFindModal}
            onCancel={() => {
              setIsLogin(false);
              setLoginCertificationVisible(false);
              dispatch(resetVerify());
              dispatch(isFinalStep(false));
            }}
          />
        </LoginCustomModal>
      )}
    </>
  )
}

export default Login;
